import { keyBy } from "lodash";

import { ILivechatGroup } from "interfaces/entities/livechat";
import { CRUDAction, IEntityFetchCollectionSuccess, IEntityFetchSuccess, RequestAction } from "store/entities/actions";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IEntityCollectionState } from "store/entities/reducer";
import { IActionWithPayload } from "store/helper";

import { INTEGRATION, IntegrationsAction } from "./actions";
import {
  IIntegration,
  ILiveChatImportDiffSuccessPayload,
  ILiveChatSelectiveImportFetchSuccessPayload,
  ILiveChatSelectiveImportStartSuccessPayload,
  IMarkLiveChatIntegrationTutorialAsVisitedSuccessPayload,
  ISyncDeleteIntegrationPayload,
} from "./interfaces";

const initialState = {};

export const integrationsReducer = (
  state: IEntityCollectionState<IIntegration> = initialState,
  action: IntegrationsAction
): IEntityCollectionState<IIntegration> => {
  switch (action.type) {
    case INTEGRATION[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IEntityFetchSuccess<IIntegration>>;
      return {
        ...state,
        [payload.id]: payload.value,
      };
    }

    case INTEGRATION[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IEntityFetchCollectionSuccess<IIntegration>>;

      // due to the way the livechat integration is handled, we need to merge the importDiff and selectiveImport data into the livechat integration
      const livechatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");

      const importDiff = livechatIntegrationId ? state[livechatIntegrationId].importDiff : null;

      const selectiveImport = livechatIntegrationId ? state[livechatIntegrationId].selectiveImport : null;

      if (livechatIntegrationId && (importDiff || selectiveImport)) {
        return {
          ...keyBy(payload.values, v => v.id),
          [livechatIntegrationId]: {
            ...(payload.values.find(v => v.id === livechatIntegrationId) as IIntegration),
            ...(importDiff ? { importDiff } : {}),
            ...(selectiveImport ? { selectiveImport } : {}),
          },
        };
      }

      return keyBy(payload.values, v => v.id);
    }

    case INTEGRATION.MARK_LIVECHAT_TUTORIAL_AS_VISITED[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IMarkLiveChatIntegrationTutorialAsVisitedSuccessPayload>;
      const integration = payload.value as IIntegration;

      // @ts-expect-error TS(2322): Type '{ [x: string]: IIntegration | { flags: { vis... Remove this comment to see the full error message
      return {
        ...state,
        [integration.id]: {
          ...state[integration.id],
          flags: {
            ...state[integration.id].flags,
            visitedLiveChatIntegrationTutorial: true,
          },
        },
      };
    }

    case INTEGRATION.SYNC_DELETE: {
      const {
        payload: { id },
      } = action as IActionWithPayload<string, ISyncDeleteIntegrationPayload>;
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(2464): A computed property name must be of type 'string',... Remove this comment to see the full error message
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _, ...rest } = state;

      return { ...rest };
    }

    case INTEGRATION.LIVECHAT_IMPORT_DIFF[RequestAction.SUCCESS]: {
      const {
        payload: { value },
      } = action as IActionWithPayload<string, ILiveChatImportDiffSuccessPayload>;

      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");

      if (!liveChatIntegrationId) {
        return state;
      }

      return {
        ...state,
        [liveChatIntegrationId]: {
          ...state[liveChatIntegrationId],
          importDiff: value,
        },
      };
    }

    case INTEGRATION.LIVECHAT_SELECTIVE_IMPORT_FETCH[RequestAction.SUCCESS]: {
      const {
        payload: { value },
      } = action as IActionWithPayload<string, ILiveChatSelectiveImportFetchSuccessPayload>;

      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");

      if (!liveChatIntegrationId) {
        return state;
      }

      return {
        ...state,
        [liveChatIntegrationId]: {
          ...state[liveChatIntegrationId],
          selectiveImport: value,
        },
      };
    }

    case INTEGRATION.LIVECHAT_SELECTIVE_IMPORT_START[RequestAction.SUCCESS]: {
      const {
        payload: { value },
      } = action as IActionWithPayload<string, ILiveChatSelectiveImportStartSuccessPayload>;

      const { data, types } = value;

      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");

      if (!liveChatIntegrationId) {
        return state;
      }

      const currentImportDiff = state[liveChatIntegrationId].importDiff;
      const newImportDiff = {
        ...currentImportDiff,
        ...types.reduce((acc, type) => {
          const currentType = currentImportDiff?.[type];
          const newType = data[type];

          if (!currentType || !newType) {
            return acc;
          }

          return {
            ...acc,
            [type]: {
              ...currentType,
              ...newType,
            },
          };
        }, {}),
      };

      // @ts-expect-error TS(2322): Type '{ [x: string]: IIntegration | { selectiveImp... Remove this comment to see the full error message
      return {
        ...state,
        [liveChatIntegrationId]: {
          ...state[liveChatIntegrationId],
          selectiveImport: data,
          importDiff: newImportDiff,
        },
      };
    }

    case INTEGRATION.FETCH_LIVECHAT_GROUPS[RequestAction.REQUEST]: {
      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");
      if (!liveChatIntegrationId) {
        return state;
      }

      const { groups } = state[liveChatIntegrationId];
      if (!groups || groups.status === "error") {
        return {
          ...state,
          [liveChatIntegrationId]: { ...state[liveChatIntegrationId], groups: { status: "loading", data: null } },
        };
      }

      return state;
    }

    case INTEGRATION.FETCH_LIVECHAT_GROUPS[RequestAction.FAILURE]: {
      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");
      if (!liveChatIntegrationId) {
        return state;
      }

      return {
        ...state,
        [liveChatIntegrationId]: { ...state[liveChatIntegrationId], groups: { status: "error", data: null } },
      };
    }

    case INTEGRATION.FETCH_LIVECHAT_GROUPS[RequestAction.SUCCESS]: {
      const { payload: groups } = action as IActionWithPayload<string, ILivechatGroup[]>;
      const liveChatIntegrationId = Object.keys(state).find(key => state[key].type === "livechat");

      if (!liveChatIntegrationId) {
        return state;
      }

      return {
        ...state,
        [liveChatIntegrationId]: {
          ...state[liveChatIntegrationId],
          groups: { status: "success", data: groups },
        },
      };
    }

    default:
      return state;
  }
};
